import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryState } from "nuqs";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import {
  getAllTransactions,
  getTransactionMetrics,
} from "../../../../legacy/network/fetchers";
import { useDebouncedValue } from "../../../hooks/use-debounced-value";
import { HeadingText, PaginationControls, Tabs } from "../../../shared/ui";
import { PAGES } from "../../../shared/lib/pageUrlConstants";
import { OtcFilterDropdown } from "../../otc-requests/components/OtcFilterDropdown";
import { TransactionMetricsSection } from "../components/TransactionMetricsSection";
import { TransactionFiltersBar } from "../components/TransactionFiltersBar";
import { TransactionsTable } from "../components/TransactionsTable";
import {
  currencySelectorOptions,
  timeFilterOptions,
} from "../constants/filterOptions";
import {
  TransactionItem,
  TransactionMainTab,
  TransactionSubTab,
} from "../types";

export const TransactionsPage: React.FC = () => {
  const navigate = useNavigate();

  // Top header state
  const [selectedCurrency, setSelectedCurrency] = useState("NGN");
  const [selectedTimeRange, setSelectedTimeRange] = useState("all");

  // Tabs & URL query state matching transactions page (?trxType=OTC / ?trxType=GENERAL / ?trxType=ALL)
  const [trxType, setTrxType] = useQueryState("trxType", {
    defaultValue: "ALL",
  });
  const normalizedTrxType = (trxType || "").toUpperCase();
  const mainTab: TransactionMainTab =
    normalizedTrxType === "OTC"
      ? "OTC"
      : normalizedTrxType === "GENERAL"
        ? "GENERAL"
        : "ALL";
  const [subTab, setSubTab] = useState<TransactionSubTab>("All");

  // Toolbar filters
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 400);
  const [sortBy, setSortBy] = useState("recent");
  const [currencyFilter, setCurrencyFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");

  // Pagination & helpers
  const [page, setPage] = useState(1);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Type parameter for backend query:
  // ALL -> sends nothing (""), General -> GENERAL, OTC -> OTC
  const activeTypeParam =
    mainTab === "OTC"
      ? "OTC"
      : mainTab === "GENERAL"
        ? "GENERAL"
        : typeFilter === "All"
          ? ""
          : typeFilter;

  // Live queries for transactions (runs for All, General and OTC tabs)
  const { data: trxData, isLoading: loadingTrx } = useQuery({
    queryKey: [
      "transactions",
      mainTab,
      activeTypeParam,
      page,
      debouncedSearchQuery,
      subTab === "All" ? "" : subTab,
      currencyFilter === "All" ? "" : currencyFilter,
      sortBy,
    ],
    queryFn: () =>
      getAllTransactions({
        page,
        sort: sortBy === "recent" ? "" : sortBy,
        type: activeTypeParam || undefined,
        status:
          subTab === "All"
            ? ""
            : subTab === "Completed"
              ? "Success"
              : subTab,
        currency: currencyFilter === "All" ? "" : currencyFilter,
        q: debouncedSearchQuery.trim() || undefined,
      }),
  });

  const { data: trxAnalytics, isLoading: loadingAnalytics } = useQuery({
    queryKey: ["transactionMetrics", selectedTimeRange, selectedCurrency],
    queryFn: () =>
      getTransactionMetrics({
        period: selectedTimeRange,
        currency: selectedCurrency,
      }),
  });

  const liveTableData: TransactionItem[] = useMemo(() => {
    return trxData?.data || [];
  }, [trxData]);

  const liveMeta = trxData?.meta;
  const totalPages = Math.max(1, liveMeta?.totalPages || 1);

  // Copy Transaction ID
  const handleCopyId = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    toast.success("Transaction ID copied to clipboard");
    setTimeout(() => setCopiedId(null), 1500);
  };

  // List of IDs for sibling navigation on detail views (matching KYB pattern)
  const transactionIds = useMemo(
    () => liveTableData.map((item) => item._id).filter(Boolean) as string[],
    [liveTableData]
  );

  // Navigate to transaction detail
  const handleRowClick = (item: TransactionItem) => {
    const isOtcItem =
      item.TransactionType === "OTC" ||
      item.type === "OTC" ||
      Boolean(item.otc);
    const detailTab =
      mainTab === "ALL"
        ? isOtcItem
          ? "OTC"
          : "General"
        : mainTab === "GENERAL"
          ? "General"
          : mainTab;

    navigate(`${PAGES.transactionsV2}/${item._id}?tab=${detailTab}`, {
      state: {
        ...item,
        tab: detailTab,
        transactionIds,
      },
    });
  };

  // Main Tabs definition: All, General & OTC
  const mainTabs = useMemo(
    () => [
      {
        label: "All",
        value: "ALL",
      },
      {
        label: "General",
        value: "GENERAL",
      },
      {
        label: "OTC",
        value: "OTC",
      },
    ],
    []
  );

  return (
    <section className="min-h-full bg-[#F3F4F8] md:p-6">
      <div className="rounded-[16px] bg-white p-4 md:p-6">
        {/* Top Header styled with KYB typography */}
        <div>
          <HeadingText className="aku-heading-h1-medium text-[#161827]">
            Transactions
          </HeadingText>

          {/* Sub header filter controls matching KYB styling */}
          <div className="mt-2.5 flex items-center gap-3 text-[14px]">
            <OtcFilterDropdown
              variant="header"
              value={selectedCurrency}
              options={currencySelectorOptions}
              onChange={setSelectedCurrency}
              icon={
                currencySelectorOptions.find(
                  (opt) => opt.value === selectedCurrency
                )?.icon
              }
              className="h-[28px] rounded-[8px] px-2 hover:bg-[#F8F9FF]"
            />

            <span className="h-4 w-px bg-[#EEF1FB]" />

            <OtcFilterDropdown
              variant="header"
              labelPrefix="Filter"
              value={selectedTimeRange}
              options={timeFilterOptions}
              onChange={setSelectedTimeRange}
              className="h-[28px] rounded-[8px] px-2 hover:bg-[#F8F9FF]"
            />
          </div>
        </div>

        {/* 3 Metric Cards Overview */}
        <TransactionMetricsSection
          loading={loadingAnalytics}
          analytics={trxAnalytics?.data || trxAnalytics}
          totalCount={liveMeta?.total ?? liveMeta?.totalItems}
          currency={selectedCurrency}
        />

        {/* Main Tabs (All, General & OTC) */}
        <Tabs
          items={mainTabs}
          value={mainTab}
          onChange={(val) => {
            setTrxType(val);
            setSubTab("All");
            setPage(1);
          }}
        />

        {/* Sub-tabs, Search & Filter Bar */}
        <TransactionFiltersBar
          subTab={subTab}
          onSubTabChange={(val) => {
            setSubTab(val);
            setPage(1);
          }}
          searchQuery={searchQuery}
          onSearchChange={(val) => {
            setSearchQuery(val);
            setPage(1);
          }}
          sortBy={sortBy}
          onSortChange={(val) => {
            setSortBy(val);
            setPage(1);
          }}
          currencyFilter={currencyFilter}
          onCurrencyChange={(val) => {
            setCurrencyFilter(val);
            setPage(1);
          }}
          typeFilter={typeFilter}
          onTypeChange={(val) => {
            setTypeFilter(val);
            setPage(1);
          }}
          onExport={() => toast.info("Exporting transaction records...")}
          isOtcTab={mainTab === "OTC"}
        />

        {/* Transactions Table */}
        <TransactionsTable
          data={liveTableData}
          isLoading={loadingTrx}
          copiedId={copiedId}
          onCopyId={handleCopyId}
          onRowClick={handleRowClick}
          isOtcTab={mainTab === "OTC"}
        />

        {/* Pagination using KYB PaginationControls */}
        <div className="mt-6 border-t border-[#F2F4F7] pt-4">
          <PaginationControls
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            className="flex items-center gap-2"
          />
        </div>
      </div>
    </section>
  );
};

export default TransactionsPage;
